/*
Fullscreen photo lightbox. Opens over the current screen, attached to
document.body so it escapes any clipping ancestor. Mirrors Android's
PhotoLightbox after v1.0.2 *Photos & progressivité*:
- Pinch-zoom 1×→5×, pan clamped to the bitmap bounds (no escape-into-
  the-corner artifact), reset to scale 1 on photo change.
- Swipe between photos when scale=1; the swipe handler is frozen as
  soon as zoom is engaged so a 1-finger drag becomes pan instead.
- Double-tap toggles between 1× and 2.5× centered on the tap point.
- Optional `onJumpToMap` button (top-left, map icon) — surfaces when
  the caller wires it; we ALSO require the photo to carry an arbreId,
  otherwise we have nothing meaningful to jump to.
- Optional `onDelete` button (top-right, trash icon) — surfaces when
  the caller wires it. The callback decides whether to return `true`
  for "deleted, advance to the next photo" (current photo is removed
  from the album in place) or `false` for "user backed out".

Why a single function instead of a stateful component:
- The lightbox is short-lived — open, view, close. No need for
  long-running state across mounts.
- Each `openLightbox` call mounts a fresh DOM tree and removes it on
  close. Object URLs passed in by the caller are NOT revoked here —
  the caller owns their lifecycle.
*/

const kSwipeCommitPx: number = 60                 // |dx| past this on release → commit nav
const kSwipeCommitFraction: number = 0.22          // OR |dx| / viewport-width past this → commit (covers small viewports)
const kSwipeAxisLockPx: number = 8                 // intent gate: ignore moves until horizontal travel exceeds this
const kSwipeOpacityFloor: number = 0.45            // figure faded to this much when fully off-screen during drag
const kSnapMs: number = 220                        // duration of the post-release snap animation

const kMinScale: number = 1                        // fit-to-screen — also the "swipe enabled" sentinel
const kMaxScale: number = 5                        // pinch ceiling, matches Compose's coerceIn(1f, 5f)
const kDoubleTapScale: number = 2.5                // toggle target when the user double-taps a region
const kDoubleTapWindowMs: number = 300             // upper bound between two taps that count as a double-tap

export interface LightboxPhoto
{
	url: string
	caption?: string
	// URL de la page source de la photo (photos de référence) : la caption
	// gagne alors un lien ↗ — parité ReferenceAttributionCaption Android
	// (IconButton OpenInNew). Absent = caption texte inchangée.
	sourceUrl?: string
	// Optional per-photo identity used by the jump-to-map button. The
	// button is hidden unless `onJumpToMap` is wired AND the active
	// photo carries an arbreId — keeping the affordance honest.
	arbreId?: number
	captureId?: number
	lat?: number
	lon?: number
}

export interface LightboxOptions
{
	// Called with the active index when the user taps the map icon.
	// Surfaces only if both this callback AND `photos[index].arbreId`
	// are present. Receiver is expected to navigate the user toward
	// the map; the lightbox closes itself before invoking.
	onJumpToMap?: (inActiveIndex: number) => void
	// Called with the active index when the user taps the trash icon.
	// Return value drives the lightbox's next state:
	//   - true  → the caller has performed the delete; the lightbox
	//             removes this photo from its local album and advances
	//             (or closes if the album becomes empty).
	//   - false → the caller backed out; lightbox stays put.
	// Throwing is treated as "false" with a console warning.
	onDelete?: (inActiveIndex: number) => Promise<boolean>
}

/*
Open the lightbox showing `inPhotos[inStartIndex]`. The lightbox is
self-contained: it builds its DOM, attaches handlers, and removes
itself on close. Returns the close function so callers can dismiss
imperatively (e.g., on screen unmount).
*/
export function openLightbox(
	inPhotos: LightboxPhoto[],
	inStartIndex: number = 0,
	inOptions: LightboxOptions = {},
): () => void
{
	if (inPhotos.length === 0) return () => { /* nothing to close */ }

	// Local copy so we can splice on delete without mutating caller state.
	const vPhotos: LightboxPhoto[] = inPhotos.slice()
	let vIndex = Math.max(0, Math.min(inStartIndex, vPhotos.length - 1))

	const vRoot = document.createElement('div')
	vRoot.className = 'lightbox'
	vRoot.setAttribute('role', 'dialog')
	vRoot.setAttribute('aria-modal', 'true')
	vRoot.innerHTML = `
		<div class="lightbox-backdrop"></div>
		<button class="lightbox-jump" aria-label="Voir sur la carte" data-visible="false">🗺️</button>
		<button class="lightbox-delete" aria-label="Supprimer" data-visible="false">🗑️</button>
		<button class="lightbox-close" aria-label="Fermer">×</button>
		<button class="lightbox-prev" aria-label="Précédente">‹</button>
		<button class="lightbox-next" aria-label="Suivante">›</button>
		<figure class="lightbox-figure">
			<div class="lightbox-zoom">
				<img class="lightbox-image" alt="" />
			</div>
			<figcaption class="lightbox-caption"></figcaption>
		</figure>
		<div class="lightbox-counter"></div>
	`
	document.body.appendChild(vRoot)
	// Lock body scroll while the lightbox is up — otherwise tapping the
	// backdrop can scroll the underlying screen on iOS.
	const vPrevOverflow = document.body.style.overflow
	document.body.style.overflow = 'hidden'

	const vFigure = vRoot.querySelector<HTMLElement>('.lightbox-figure')
	const vZoom = vRoot.querySelector<HTMLElement>('.lightbox-zoom')
	const vImg = vRoot.querySelector<HTMLImageElement>('.lightbox-image')

	// Zoom state — per-photo, reset on every navigate / commit / delete.
	let vScale: number = kMinScale  // current scale, kept in [kMinScale, kMaxScale]
	let vTx: number = 0  // pan x (CSS px), clamped to ±maxPanX
	let vTy: number = 0  // pan y (CSS px), clamped to ±maxPanY
	let vNatW: number = 0  // intrinsic bitmap width, populated on load — used by clampOffset
	let vNatH: number = 0  // intrinsic bitmap height
	let vLastTapTs: number = 0  // for double-tap detection
	let vLastTapX: number = 0
	let vLastTapY: number = 0

	const vClose = (): void => {
		document.body.style.overflow = vPrevOverflow
		document.removeEventListener('keydown', vOnKey)
		vRoot.remove()
	}

	const vOnKey = (inEv: KeyboardEvent): void => {
		if (inEv.key === 'Escape') vClose()
		else if (inEv.key === 'ArrowLeft') vNavigate(-1)
		else if (inEv.key === 'ArrowRight') vNavigate(+1)
	}
	document.addEventListener('keydown', vOnKey)

	const vNavigate = (inDelta: number): void => {
		const vNew = vIndex + inDelta
		if (vNew < 0 || vNew >= vPhotos.length) return
		vIndex = vNew
		vResetZoom()
		vPaint()
	}

	/*
	Compute the maximum allowable |tx|/|ty| in CSS px for the current
	scale + bitmap size + viewport. Formula mirrors Android's
	clampOffset (PhotoLightbox.kt l. 218-234):
	  fitScale = min(boxW/imgW, boxH/imgH)
	  fitW     = imgW * fitScale
	  fitH     = imgH * fitScale
	  maxPanX  = max(0, (fitW * scale - boxW) / 2)
	  maxPanY  = max(0, (fitH * scale - boxH) / 2)
	The "demi-débordement du bitmap zoomed" interpretation keeps the
	image edges flush against the viewport instead of letting them slide
	into a corner once the zoom is undone.
	*/
	const vClampPan = (): void => {
		if (!vFigure || vNatW === 0 || vNatH === 0) return
		const vBoxW = vFigure.clientWidth
		const vBoxH = vFigure.clientHeight
		if (vBoxW === 0 || vBoxH === 0) return
		const vFit = Math.min(vBoxW / vNatW, vBoxH / vNatH)  // ratio that fits the bitmap inside the figure with letterboxing
		const vFitW = vNatW * vFit
		const vFitH = vNatH * vFit
		const vMaxX = Math.max(0, (vFitW * vScale - vBoxW) / 2)  // 0 at scale=1, grows linearly with scale
		const vMaxY = Math.max(0, (vFitH * vScale - vBoxH) / 2)
		if (vTx > vMaxX) vTx = vMaxX
		else if (vTx < -vMaxX) vTx = -vMaxX
		if (vTy > vMaxY) vTy = vMaxY
		else if (vTy < -vMaxY) vTy = -vMaxY
	}

	const vApplyTransform = (inAnimate: boolean): void => {
		if (!vZoom) return
		vZoom.style.transition = inAnimate ? `transform ${kSnapMs}ms ease` : 'none'
		vZoom.style.transform = `translate(${vTx}px, ${vTy}px) scale(${vScale})`
	}

	const vResetZoom = (): void => {
		vScale = kMinScale
		vTx = 0
		vTy = 0
		vApplyTransform(false)
	}

	const vSetScale = (inNext: number, inAnimate: boolean): void => {
		vScale = Math.max(kMinScale, Math.min(kMaxScale, inNext))
		if (vScale === kMinScale) { vTx = 0; vTy = 0 }
		else vClampPan()
		vApplyTransform(inAnimate)
	}

	const vPaint = (): void => {
		const vPhoto = vPhotos[vIndex]
		const vCap = vRoot.querySelector<HTMLElement>('.lightbox-caption')
		const vCounter = vRoot.querySelector<HTMLElement>('.lightbox-counter')
		const vPrev = vRoot.querySelector<HTMLButtonElement>('.lightbox-prev')
		const vNext = vRoot.querySelector<HTMLButtonElement>('.lightbox-next')
		const vJump = vRoot.querySelector<HTMLButtonElement>('.lightbox-jump')
		const vDelete = vRoot.querySelector<HTMLButtonElement>('.lightbox-delete')
		if (vImg)
		{
			vImg.src = vPhoto.url
			vImg.onload = () => {
				// Read intrinsic size for the pan-clamp formula. Idempotent
				// to set on every load — no listener stack-up.
				vNatW = vImg.naturalWidth || 0
				vNatH = vImg.naturalHeight || 0
				vClampPan()
				vApplyTransform(false)
			}
		}
		if (vCap)
		{
			// textContent d'abord (les captions portent des noms d'auteurs
			// arbitraires — jamais d'innerHTML), il purge aussi le lien de
			// la photo précédente ; puis le ↗ source est append en DOM API.
			vCap.textContent = vPhoto.caption ?? ''
			if (vPhoto.sourceUrl)
			{
				const vLink = document.createElement('a')
				vLink.className = 'lightbox-caption-link'
				vLink.href = vPhoto.sourceUrl
				vLink.target = '_blank'
				vLink.rel = 'noopener'
				vLink.setAttribute('aria-label', 'Ouvrir la source')
				vLink.textContent = '↗'
				// La caption vit dans la figure, qui porte les handlers
				// swipe/double-tap : on isole le lien du stream de gestes
				// (un double-tap sur ↗ ne doit pas zoomer, un tap ne doit
				// pas armer le tracking swipe). Le dismiss n'est pas
				// concerné — il vit sur le backdrop, sibling de la figure.
				vLink.addEventListener('touchstart', (inEv) => inEv.stopPropagation(), { passive: true })
				vLink.addEventListener('click', (inEv) => inEv.stopPropagation())
				vCap.appendChild(vLink)
			}
		}
		if (vCounter) vCounter.textContent = `${vIndex + 1} / ${vPhotos.length}`
		if (vPrev) vPrev.disabled = vIndex === 0
		if (vNext) vNext.disabled = vIndex === vPhotos.length - 1
		// Top-bar buttons follow data-visible semantics so CSS can fade
		// them out cleanly when they don't apply for the active photo.
		if (vJump) vJump.dataset.visible = (inOptions.onJumpToMap && typeof vPhoto.arbreId === 'number') ? 'true' : 'false'
		if (vDelete) vDelete.dataset.visible = inOptions.onDelete ? 'true' : 'false'
	}

	vRoot.querySelector('.lightbox-close')?.addEventListener('click', vClose)
	vRoot.querySelector('.lightbox-backdrop')?.addEventListener('click', vClose)
	vRoot.querySelector('.lightbox-prev')?.addEventListener('click', () => vNavigate(-1))
	vRoot.querySelector('.lightbox-next')?.addEventListener('click', () => vNavigate(+1))

	vRoot.querySelector('.lightbox-jump')?.addEventListener('click', (inEv) => {
		inEv.stopPropagation()
		const vPhoto = vPhotos[vIndex]
		if (!inOptions.onJumpToMap || typeof vPhoto?.arbreId !== 'number') return
		const vCb = inOptions.onJumpToMap  // capture before close so the next-tick navigation can still resolve
		const vActive = vIndex
		vClose()
		// Defer one tick so the lightbox unmount lands before navigation
		// — avoids a paint of the new screen behind the still-closing
		// dialog on iOS Safari.
		setTimeout(() => vCb(vActive), 0)
	})

	vRoot.querySelector('.lightbox-delete')?.addEventListener('click', async (inEv) => {
		inEv.stopPropagation()
		if (!inOptions.onDelete) return
		let vConfirmed = false
		try { vConfirmed = await inOptions.onDelete(vIndex) }
		catch (vErr) { console.warn('Lightbox onDelete threw', vErr); vConfirmed = false }
		if (!vConfirmed) return
		// Splice the active photo out and either advance or close.
		vPhotos.splice(vIndex, 1)
		if (vPhotos.length === 0) { vClose(); return }
		if (vIndex >= vPhotos.length) vIndex = vPhotos.length - 1
		vResetZoom()
		vPaint()
	})

	// Touch-pinch + pan + swipe. We handle three modes on a single
	// gesture stream:
	//   - 2 fingers   → pinch-zoom (always consumes); pan also updates
	//                   so the centroid stays under the user's fingers
	//   - 1 finger, scale=1 → horizontal swipe between photos
	//   - 1 finger, scale>1 → pan (clamped)
	// On touchend, swipe commits or springs back; pan stays where it
	// is; pinch leaves scale where it is.
	let vTouchStartX: number | null = null  // touch origin x for the swipe handler
	let vTouchStartY: number | null = null  // touch origin y for the axis-lock decision
	let vDragLocked: boolean = false  // true once we've decided this gesture is a horizontal swipe
	let vDx: number = 0  // current signed swipe delta in px
	let vPanStartX: number = 0  // pointer origin for the 1-finger pan
	let vPanStartY: number = 0
	let vPanBaseTx: number = 0  // tx at gesture start so move can be cumulative
	let vPanBaseTy: number = 0
	let vPinchStartDist: number = 0  // initial finger distance for the pinch ratio
	let vPinchBaseScale: number = 1  // scale at the moment 2-finger touch began
	let vPinchActive: boolean = false  // sticky for the gesture's lifetime, even past temporary 1-finger frames

	const vSetSwipeTransform = (inDx: number, inAnimate: boolean): void => {
		if (!vFigure) return
		vFigure.style.transition = inAnimate ? `transform ${kSnapMs}ms ease, opacity ${kSnapMs}ms ease` : 'none'
		vFigure.style.transform = `translateX(${inDx}px)`
		const vWidth = vFigure.offsetWidth || 1
		const vRatio = Math.min(1, Math.abs(inDx) / vWidth)
		vFigure.style.opacity = String(1 - vRatio * (1 - kSwipeOpacityFloor))
	}

	const vResetSwipeTransform = (inAnimate: boolean): void => { vSetSwipeTransform(0, inAnimate) }

	const vDist = (inA: Touch, inB: Touch): number => {
		const vDxLocal = inA.clientX - inB.clientX
		const vDyLocal = inA.clientY - inB.clientY
		return Math.hypot(vDxLocal, vDyLocal)
	}

	vFigure?.addEventListener('touchstart', (inEv: TouchEvent) => {
		const vTouches = inEv.touches
		if (vTouches.length === 2)
		{
			// 2 fingers → start pinch. Cancel any in-flight swipe.
			vPinchActive = true
			vPinchStartDist = vDist(vTouches[0]!, vTouches[1]!) || 1
			vPinchBaseScale = vScale
			vTouchStartX = null
			vTouchStartY = null
			vDragLocked = false
			vDx = 0
			vSetSwipeTransform(0, false)
			return
		}
		const vT = vTouches[0]
		if (!vT) return
		if (vScale > kMinScale)
		{
			// 1 finger zoomed → pan mode. Note the cumulative start.
			vPanStartX = vT.clientX
			vPanStartY = vT.clientY
			vPanBaseTx = vTx
			vPanBaseTy = vTy
			vTouchStartX = null
			vTouchStartY = null
		}
		else
		{
			// 1 finger at base scale → swipe mode (existing behaviour).
			vTouchStartX = vT.clientX
			vTouchStartY = vT.clientY
			vDragLocked = false
			vDx = 0
			vSetSwipeTransform(0, false)

			// Double-tap detector. Trips on the SECOND tap inside the
			// double-tap window; resets when the gap is too long.
			const vNow = performance.now()
			const vWithinWindow = (vNow - vLastTapTs) < kDoubleTapWindowMs
			const vWithinJitter = Math.abs(vT.clientX - vLastTapX) < 30 && Math.abs(vT.clientY - vLastTapY) < 30
			if (vWithinWindow && vWithinJitter)
			{
				// Toggle 1× ↔ 2.5× (matches Android's double-tap behaviour).
				const vNext = vScale === kMinScale ? kDoubleTapScale : kMinScale
				vSetScale(vNext, true)
				vLastTapTs = 0  // consume so a third tap doesn't immediately re-toggle
			}
			else
			{
				vLastTapTs = vNow
				vLastTapX = vT.clientX
				vLastTapY = vT.clientY
			}
		}
	}, { passive: true })

	vFigure?.addEventListener('touchmove', (inEv: TouchEvent) => {
		const vTouches = inEv.touches
		if (vPinchActive && vTouches.length >= 2)
		{
			const vNow = vDist(vTouches[0]!, vTouches[1]!) || 1
			const vRatio = vNow / vPinchStartDist
			vSetScale(vPinchBaseScale * vRatio, false)
			if (inEv.cancelable) inEv.preventDefault()
			return
		}
		if (vScale > kMinScale)
		{
			const vT = vTouches[0]
			if (!vT) return
			vTx = vPanBaseTx + (vT.clientX - vPanStartX)
			vTy = vPanBaseTy + (vT.clientY - vPanStartY)
			vClampPan()
			vApplyTransform(false)
			if (inEv.cancelable) inEv.preventDefault()
			return
		}
		// Swipe path — only valid at scale=1.
		if (vTouchStartX === null || vTouchStartY === null) return
		const vT = vTouches[0]
		if (!vT) return
		const vRawDx = vT.clientX - vTouchStartX
		const vRawDy = vT.clientY - vTouchStartY
		if (!vDragLocked)
		{
			if (Math.abs(vRawDx) < kSwipeAxisLockPx && Math.abs(vRawDy) < kSwipeAxisLockPx) return
			if (Math.abs(vRawDy) > Math.abs(vRawDx))
			{
				vTouchStartX = null
				vTouchStartY = null
				return
			}
			vDragLocked = true
		}
		const vAtLeftEdge = vIndex === 0 && vRawDx > 0
		const vAtRightEdge = vIndex === vPhotos.length - 1 && vRawDx < 0
		vDx = (vAtLeftEdge || vAtRightEdge) ? vRawDx * 0.4 : vRawDx
		if (inEv.cancelable) inEv.preventDefault()
		vSetSwipeTransform(vDx, false)
	}, { passive: false })

	vFigure?.addEventListener('touchend', (inEv: TouchEvent) => {
		// Released a pinch gesture (or one of the two fingers came up
		// during it). End pinch mode, leave the scale where the user
		// stopped pinching.
		if (vPinchActive && inEv.touches.length < 2)
		{
			vPinchActive = false
			return
		}
		if (vScale > kMinScale)
		{
			// Pan release — no commit, just keep where it is.
			return
		}
		if (vTouchStartX === null) { vDx = 0; return }
		const vWidth = vFigure?.offsetWidth || 1
		const vCommit = Math.abs(vDx) > kSwipeCommitPx || Math.abs(vDx) / vWidth > kSwipeCommitFraction
		const vTargetDelta = vDx < 0 ? +1 : -1
		const vNeighbourExists = (vIndex + vTargetDelta) >= 0 && (vIndex + vTargetDelta) < vPhotos.length
		vTouchStartX = null
		vTouchStartY = null

		if (vCommit && vNeighbourExists)
		{
			const vExitDx = vDx < 0 ? -vWidth : vWidth
			const vOnEnd = (): void => {
				vFigure?.removeEventListener('transitionend', vOnEnd)
				vIndex = vIndex + vTargetDelta
				vResetZoom()
				vPaint()
				vSetSwipeTransform(0, false)
			}
			vFigure?.addEventListener('transitionend', vOnEnd, { once: true })
			vSetSwipeTransform(vExitDx, true)
		}
		else
		{
			vResetSwipeTransform(true)
		}
		vDx = 0
	})

	vFigure?.addEventListener('touchcancel', () => {
		vPinchActive = false
		vTouchStartX = null
		vTouchStartY = null
		vDx = 0
		vResetSwipeTransform(true)
	})

	// Desktop fallback : Ctrl/Cmd + mouse wheel zoom (mirrors photo
	// viewers most users already know). Plain scroll is ignored so the
	// page scrolls behind the lightbox when it's not engaged.
	vFigure?.addEventListener('wheel', (inEv: WheelEvent) => {
		if (!(inEv.ctrlKey || inEv.metaKey)) return
		inEv.preventDefault()
		const vDelta = -inEv.deltaY * 0.005  // negative deltaY = scroll up = zoom in
		vSetScale(vScale + vDelta, false)
	}, { passive: false })

	vPaint()
	return vClose
}
